import { Filter } from '@/components/ats';
import React, { useCallback, useMemo } from 'react';
import { useRequisitionFiltersConfig } from './useRequisitionFiltersConfig';

const hasFilterValues = (filter: any): boolean =>
  filter.filter_by_values !== undefined &&
  filter.filter_by_values !== null &&
  (Array.isArray(filter.filter_by_values)
    ? filter.filter_by_values.length > 0
    : true);

// For New List Page
interface NewRequisitionFiltersProps {
  filters: any[];
  sort: { element: string; order: 'asc' | 'desc'; label: string };
  searchTerm: string;
  currentView?: string;
  onFiltersChange: (_filters: any[]) => void;
  onSortChange: (_sort: any) => void;
  onSearchChange: (_searchTerm: string) => void;
  onViewChange?: (_view?: string) => void;
  filterIds?: string[];
  isListPage?: boolean;
  associateOfferHireStatus?: boolean;
  associateJobStatus?: boolean;
  disabled?: boolean;
}

export const RequisitionFilters: React.FC<NewRequisitionFiltersProps> = (
  props
) => {
  const isListPage = props.isListPage;
  const filters = props.filters;
  const sort = props.sort;
  const searchTerm = props.searchTerm;
  const currentView = props.currentView;
  const associateOfferHireStatus = props.associateOfferHireStatus || false;
  const associateJobStatus = props.associateJobStatus || false;

  // Derived state: compute from filters prop during render (no useEffect needed)
  const filterWithValues = useMemo(
    () => filters.filter(hasFilterValues),
    [filters]
  );

  const { filterConfig, sortOptions } = useRequisitionFiltersConfig(
    props.filterIds,
    props.isListPage,
    associateOfferHireStatus,
    associateJobStatus
  );

  const { onFiltersChange, onSortChange, onSearchChange, onViewChange } = props;

  const filterActions = useCallback(
    (filterData: any) => {
      const actions = {
        apply: ({ values }: { values: any[] }) => onFiltersChange(values),
        clearAll: () => onFiltersChange([]),
        sort: ({ option }: { option: any }) => onSortChange(option),
        search: ({ searchTerm }: { searchTerm: string }) =>
          onSearchChange(searchTerm),
        view: ({ view }: { view: string }) => onViewChange(view),
        default: () => console.log('Unhandled filter action'),
      };

      const action = actions[filterData.type] || actions.default;
      action(filterData);
    },
    [onFiltersChange, onSortChange, onSearchChange, onViewChange]
  );

  const filterProps = React.useMemo(
    () => ({
      filterActions: filterActions,
      activeFilters: filters,
      isSortEnabled: true,
      sortOptions: sortOptions,
      sort: sort,
      isSearchEnabled: true,
      hideSearchFullView: false,
      searchTerm,
      filterConfig,
      disabled: props.disabled,
    }),
    [
      filterActions,
      filters,
      sortOptions,
      sort,
      searchTerm,
      filterConfig,
      props.disabled,
    ]
  );

  return (
    <Filter
      {...filterProps}
      filterContainerProps={{
        width:
          associateJobStatus || associateOfferHireStatus ? '220px' : 'auto',
      }}
      selected={filterWithValues}
      isNewFilter={true}
      searchProps={{
        inputGroupStyles: {
          w: '250px',
        },
        placeholder: 'Search by ID, role name',
      }}
      enableToggleView={isListPage}
      currentView={currentView}
      accordionTitle="Requisition Filters"
      showAccordion={false}
    />
  );
};
